import React, { useState, useEffect } from 'react';
import { useEmailSubscriptionQuery } from '../data/useEmailSubscriptionQuery';
import { useSaveEmailSubscriptionMutation } from '../data/useEmailSubscriptionMutation';
import type { EmailSubscriptionSettings } from '../types';

interface UseEmailSubscriptionOptions {
  userAuthUuid: string | undefined;
  initialSettings: EmailSubscriptionSettings;
}

interface UseEmailSubscriptionReturn {
  settings: EmailSubscriptionSettings;
  setSettings: React.Dispatch<React.SetStateAction<EmailSubscriptionSettings>>;
  saving: boolean;
  save: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for managing user email subscription settings
 *
 * Provides email subscription settings management with User API integration.
 * Uses TanStack Query for data fetching and mutations.
 *
 * @param options - Configuration options
 * @param options.userAuthUuid - The auth service UUID of the user
 * @param options.initialSettings - Initial email subscription settings
 * @returns Email subscription settings and management functions
 */
export const useEmailSubscription = ({
  userAuthUuid,
  initialSettings,
}: UseEmailSubscriptionOptions): UseEmailSubscriptionReturn => {
  const [settings, setSettings] = useState<EmailSubscriptionSettings>(initialSettings);

  // Query for fetching email subscription settings
  const { data, isLoading, error } = useEmailSubscriptionQuery(userAuthUuid);

  // Mutation for saving email subscription settings
  const saveMutation = useSaveEmailSubscriptionMutation();

  // Update local settings when query data changes
  useEffect(() => {
    if (data) {
      setSettings(data);
    }
  }, [data]);

  // Also update from initial settings if no query data yet
  useEffect(() => {
    if (!data && initialSettings) {
      setSettings(initialSettings);
    }
  }, [initialSettings, data]);

  const save = async () => {
    if (!userAuthUuid) return;

    await saveMutation.mutateAsync({
      userAuthUuid,
      settings,
    });
  };

  return {
    settings,
    setSettings,
    saving: saveMutation.isPending,
    save,
    isLoading,
    error: error as Error | null,
  };
};
